import {
  Alert,
  Button,
  Divider,
  Sidebar,
  UntitledLoading,
  useToastContext,
  H3,
  H4,
  Prose,
  Text,
} from '@payaca/ambrosia';
import ShadowDomRenderer from '@payaca/components/shadowDomRenderer/ShadowDomRenderer';
import { CustomFieldDefinitionPreview } from '@payaca/custom-fields-react';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import useCloneDocumentBlueprint from '@/api/mutations/documentBlueprint/useCloneDocumentBlueprint';
import useGetDocumentBlueprint, {
  DocumentBlueprint,
} from '@/api/queries/documentBlueprints/useGetDocumentBlueprint';
import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { useChatAgent } from '@/ui/components/chatAgent/ChatAgentContextProvider';
import { useHistory } from 'react-router';

const DocumentBlueprintPreviewPage: FC = () => {
  const params = useParams<{ blueprintId?: string }>();
  const blueprintId = params.blueprintId;
  const isMobile = !useIsContainerBeyondBreakpoint('md');

  const [isPreviewSidebarOpen, setIsPreviewSidebarOpen] = useState(false);

  // The preview and its own sidebar need the full width, so close the chat
  // panel while this page is open. The document template pages reopen it
  // through their bindings.
  const { hideChatAgent } = useChatAgent();
  useEffect(() => {
    hideChatAgent();
  }, [hideChatAgent]);

  const { blueprint, isLoading } = useGetDocumentBlueprint(blueprintId);

  const breadcrumbItems = [
    { label: 'Settings', to: '/companySettings' },
    {
      label: 'Ready-made library',
      to: '/companySettings/custom-data/ready-made-library',
    },
    { label: blueprint?.name ?? 'Document' },
  ];

  if (isLoading) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <UntitledLoading />
      </AuthenticatedPageWrapper>
    );
  }

  if (!blueprint) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <div className="p-4">
          <Alert colour="red">This library item wasn&apos;t found.</Alert>
        </div>
      </AuthenticatedPageWrapper>
    );
  }

  if (isMobile) {
    return (
      <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
        <div className="space-y-2.5 p-3.5 h-full flex flex-col min-h-0">
          <DetailContent
            blueprint={blueprint}
            onPreviewClick={() => setIsPreviewSidebarOpen((s) => !s)}
          />
        </div>

        <Sidebar
          isOpen={isPreviewSidebarOpen}
          onClose={() => setIsPreviewSidebarOpen(false)}
        >
          <ShadowDomRenderer
            html={blueprint.documentPreviewHtml}
            className="pointer-events-none overflow-hidden"
            data-testid="document-blueprint-preview"
          />
        </Sidebar>
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
      <div className="grid grid-cols-3 min-h-0 h-full">
        <div className="flex overflow-y-auto p-8 col-span-2 bg-gray-50">
          <Prose className="max-w-6xl w-full mx-auto">
            <ShadowDomRenderer
              html={blueprint.documentPreviewHtml}
              className="pointer-events-none overflow-hidden rounded-lg bg-white shadow-md border"
              data-testid="document-blueprint-preview"
            />
          </Prose>
        </div>

        <div className="border-l overflow-y-auto min-h-0 h-full p-3.5 space-y-2.5">
          <DetailContent blueprint={blueprint} />
        </div>
      </div>
    </AuthenticatedPageWrapper>
  );
};

const AddToAccountButton: FC<{
  blueprint: DocumentBlueprint;
}> = (props) => {
  const { blueprint } = props;

  const { pushSuccess } = useToastContext();
  const history = useHistory();
  const { mutate: clone, isPending: isCloning } = useCloneDocumentBlueprint({
    onSuccess: (data) => {
      pushSuccess(
        'Added to your account. The fieldset and document template are now available.'
      );
      history.push(
        `/templates/document-templates/edit/${data.cloneDocumentBlueprint.documentTemplate.id}`
      );
    },
  });

  return (
    <Button
      size="sm"
      className="w-full"
      isProcessing={isCloning}
      disabled={blueprint.addedToAccount}
      onClick={() => clone(blueprint.id)}
    >
      {blueprint.addedToAccount ? 'Added' : 'Add to account'}
    </Button>
  );
};

const DetailContent: FC<{
  blueprint: DocumentBlueprint;
  onPreviewClick?: () => void;
}> = (props) => {
  const { blueprint, onPreviewClick } = props;

  const isMobile = !useIsContainerBeyondBreakpoint('md');

  return (
    <>
      <H3>{blueprint.name}</H3>
      {blueprint.description && <Text>{blueprint.description}</Text>}
      {!isMobile && <AddToAccountButton blueprint={blueprint} />}
      <Divider className="my-4" />
      <H4>Included project custom fieldsets</H4>
      <div className="mb-4">
        <Text colour="muted">
          These custom fieldsets will become available and can be set on each
          project page
        </Text>
      </div>
      <CustomFieldDefinitionPreview
        customFields={blueprint.fieldGroup.customFields}
        emptyText="No fields in this fieldset."
      />
      <div className="space-y-2.5 mt-auto">
        {!!onPreviewClick && (
          <Button
            className="w-full"
            size="sm"
            variant="secondary"
            onClick={onPreviewClick}
          >
            Preview document
          </Button>
        )}
        {isMobile && <AddToAccountButton blueprint={blueprint} />}
      </div>
    </>
  );
};

export default DocumentBlueprintPreviewPage;
